import { useState, useRef, useCallback, useEffect } from 'react'
import type { TMedicalTeamComment } from './medical-team-comments.type'
import MedicalTeamCommentInput from './medical-team-comment-input.component'
import MedicalTeamCommentList from './medical-team-comment-list.component'
import { CurecallConfirmationModal, ECurecallNotificationAppearance } from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useNotification } from 'src/providers/notification.provider'
import { useCurrentProject, WithPermission } from 'src/providers/current-project.provider'
import {
  EPermissionGrantActions,
  EPermissionGrantSubjects,
} from 'src/pages/configuration/permissions/_components/permissions.type'
import { PatientService } from 'src/services/patient.service'
import { useSocket } from 'src/providers/socket.provider'

export type TMedicalTeamCommentProps = {
  patientId: number
  medicalTeamComments: TMedicalTeamComment[]
  onUpdateMedicalTeamComment: (patientId: number, medicalTeamComment: TMedicalTeamComment[]) => void
  patientFollowupId?: number
  patientMedEventId?: number
  isPatientFollowupEnded?: boolean
}

type TSelectedComment = {
  id: number
  comment: string
}

const MedicalTeamComment = ({
  patientId,
  medicalTeamComments,
  onUpdateMedicalTeamComment,
  patientMedEventId,
  patientFollowupId,
  isPatientFollowupEnded,
}: TMedicalTeamCommentProps) => {
  const { project, hasPermission } = useCurrentProject()
  const canWrite = hasPermission(EPermissionGrantSubjects.PATIENT, EPermissionGrantActions.UPDATE)
  const { t } = useTranslation()
  const { socket, socketError } = useSocket()
  const { notify } = useNotification()
  const [selectedComment, setSelectedComment] = useState<TSelectedComment | undefined>(undefined)
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState<boolean>(false)
  const [commentToDelete, setCommentToDelete] = useState<TMedicalTeamComment | undefined>(undefined)

  const medicalTeamCommentComponentStart = useRef<HTMLDivElement | null>(null)

  // helpers function
  const addOrUpdateComment = (commentToAddOrUpdate: TMedicalTeamComment) => {
    if (!medicalTeamComments) return
    const isCommentAlreadyAdded = medicalTeamComments.some(
      (comment) => comment.id === commentToAddOrUpdate.id,
    )
    const commentListUpdated = isCommentAlreadyAdded
      ? medicalTeamComments.map((comment) =>
          comment.id === commentToAddOrUpdate.id ? commentToAddOrUpdate : comment,
        )
      : [...medicalTeamComments, commentToAddOrUpdate]
    onUpdateMedicalTeamComment(patientId, commentListUpdated)
  }

  const deleteComment = (commentIdToDelete: number) => {
    if (!medicalTeamComments) return
    const commentListUpdated = medicalTeamComments.filter(
      (medicalComment) => medicalComment.id !== commentIdToDelete,
    )
    setSelectedComment(undefined)
    onUpdateMedicalTeamComment(patientId, commentListUpdated)
  }

  const updateMedicalTeamCommentList = useCallback(
    (data: any) => {
      const medicalTeamComment = data.medicalTeamComment
      if (!medicalTeamComment) return
      if (data.changeType === 'CREATED' || data.changeType === 'UPDATED') {
        addOrUpdateComment(medicalTeamComment)
      }
      if (data.changeType === 'DELETED') {
        deleteComment(medicalTeamComment.id)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  // socket
  useEffect(() => {
    if (!socket || !patientId || socketError) return undefined
    socket.emit('SUBSCRIBE_PATIENT', { patientId }) // this is required because this modale can be opened from patients list
    socket.on('MEDICAL_TEAM_COMMENT', updateMedicalTeamCommentList)
    return () => {
      socket.emit('UNSUBSCRIBE_PATIENT', { patientId })
      socket.off('MEDICAL_TEAM_COMMENT', updateMedicalTeamCommentList)
    }
  }, [patientId, socket, socketError, updateMedicalTeamCommentList])

  // handlers
  const handleSelectComment = (comment: TSelectedComment) => {
    setSelectedComment(comment)
    setTimeout(() => {
      medicalTeamCommentComponentStart.current?.scrollIntoView?.({
        behavior: 'smooth',
      })
    }, 300)
  }

  const handleAddOrUpdateComment = async (newComment: string) => {
    const payload = {
      comment: newComment,
      patientFollowupId,
      patientMedEventId,
    }
    if (selectedComment) {
      try {
        const result = await PatientService.putPatientComment(
          project.id,
          patientId,
          selectedComment.id,
          payload,
        )
        addOrUpdateComment(result)
        setSelectedComment(undefined)
        notify({
          title: t('components.medical-team-comments.comment-updated-title'),
          message: t('components.medical-team-comments.comment-updated-message'),
          appearance: ECurecallNotificationAppearance.SUCCESS,
        })
      } catch {
        notify({
          title: t('components.medical-team-comments.comment-not-updated-title'),
          message: t('components.medical-team-comments.comment-not-updated-message'),
          appearance: ECurecallNotificationAppearance.ERROR,
        })
      }
    } else {
      try {
        const result = await PatientService.postPatientComment(project.id, patientId, payload)
        addOrUpdateComment(result)
        setSelectedComment(undefined)
        notify({
          title: t('components.medical-team-comments.comment-added-title'),
          message: t('components.medical-team-comments.comment-added-message'),
          appearance: ECurecallNotificationAppearance.SUCCESS,
        })
      } catch {
        notify({
          title: t('components.medical-team-comments.comment-not-added-title'),
          message: t('components.medical-team-comments.comment-not-added-message'),
          appearance: ECurecallNotificationAppearance.ERROR,
        })
      }
    }
  }

  const handleClickDeleteCommentButton = async (commentToDelete: TMedicalTeamComment) => {
    setIsOpenConfirmModal(true)
    setCommentToDelete(commentToDelete)
  }

  const handleConfirmDeleteComment = async (commentId: number) => {
    try {
      await PatientService.deletePatientComment(project.id, patientId, commentId)
      deleteComment(commentId)
      notify(
        {
          title: t('components.medical-team-comments.comment-deleted-title'),
          message: t('components.medical-team-comments.comment-deleted-message'),
          appearance: ECurecallNotificationAppearance.WARNING,
        },
        6000,
      )
    } catch {
      notify({
        title: t('components.medical-team-comments.comment-not-deleted-title'),
        message: t('components.medical-team-comments.comment-not-deleted-message'),
        appearance: ECurecallNotificationAppearance.ERROR,
      })
    }
  }

  const filteredMedicalTeamComments = patientFollowupId
    ? medicalTeamComments.filter(
        (medicalTeamComment) => medicalTeamComment.PatientFollowupId === patientFollowupId,
      )
    : medicalTeamComments

  return (
    <div className="c-relative c-pr-4 c-flex c-flex-col c-overflow-auto">
      <main ref={medicalTeamCommentComponentStart} className="c-absolute c-top-[-16px] c-p-0" />
      {!isPatientFollowupEnded && (
        <WithPermission
          requiredSubject={EPermissionGrantSubjects.PATIENT}
          requiredAction={EPermissionGrantActions.UPDATE}
        >
          <MedicalTeamCommentInput
            defaultValue={selectedComment?.comment}
            onPostComment={handleAddOrUpdateComment}
            onCancel={() => setSelectedComment(undefined)}
          />
        </WithPermission>
      )}
      <MedicalTeamCommentList
        medicalTeamCommentList={filteredMedicalTeamComments}
        selectedComment={selectedComment}
        onDeleteComment={handleClickDeleteCommentButton}
        onSelectComment={handleSelectComment}
        canWrite={canWrite}
        isPatientFollowupEnded={isPatientFollowupEnded}
      />
      {commentToDelete && (
        <CurecallConfirmationModal
          color="ALERT"
          title={t('components.medical-team-comments.confirmation-modal-title')}
          isOpen={isOpenConfirmModal}
          cancelButtonLabel={t('components.modals.cancel')}
          confirmButtonLabel={t('components.modals.confirm')}
          onCancel={() => setIsOpenConfirmModal(false)}
          onConfirm={() => {
            handleConfirmDeleteComment(commentToDelete.id)
            setIsOpenConfirmModal(false)
          }}
        >
          {}
          &laquo; {commentToDelete.comment} &raquo;
        </CurecallConfirmationModal>
      )}
    </div>
  )
}

export default MedicalTeamComment
